import { z } from 'zod';
import { auditSchema, idSchema, isoDateTimeSchema } from '../../common.js';
/** Yjs doc holding `Y.Map<listId, ListMeta>` (ROADMAP §1.3). */
export const LISTS_DOC = 'lists';
/** One Yjs doc per list: order array + `Y.Map<itemId, ListItem>`. */
export function listDocName(listId) {
    return `list:${listId}`;
}
export const listTypeSchema = z.enum(['todo', 'shopping']);
export const listMetaSchema = auditSchema.extend({
    id: idSchema,
    name: z.string().min(1).max(200),
    type: listTypeSchema,
    /** Shopping lists may be scoped to a store ("Costco") for store splitting. */
    store: z.string().max(200).optional(),
    emoji: z.string().max(8).optional(),
    archived: z.boolean(),
    /** Group items under `section` headers (store aisles for shopping, groups for to-do). */
    sectionsEnabled: z.boolean().optional(),
});
export const listItemSchema = auditSchema.extend({
    id: idSchema,
    text: z.string().max(2000),
    checked: z.boolean(),
    checkedBy: idSchema.optional(),
    checkedAt: isoDateTimeSchema.optional(),
    assigneeIds: z.array(idSchema).max(16),
    /** When true the item renders as a section header rather than a checkable row. */
    header: z.boolean().optional(),
    /**
     * Fractional sort key (ROADMAP §1.3 "Y.Array of item ids for order (fractional-index
     * fallback)"). The `order` Y.Array in the list doc tracks *which* ids exist; this field
     * is the source of truth for *display order* — see `orderBetween()`. Reordering an item
     * is therefore a single `Y.Map` field write (LWW, always safe), never a structural
     * Y.Array move, which is what keeps concurrent reorder-vs-delete/edit lossless.
     */
    order: z.number(),
    /** Store aisle / category grouping for shopping-mode section headers and store splits. */
    section: z.string().max(100).optional(),
    /** Freeform quantity/detail shown as the item's subtitle ("6 lb", "×6", "for the chili"). */
    detail: z.string().max(300).optional(),
});
/**
 * Fractional-index generator for item ordering. `before`/`after` are neighboring items'
 * `order` values (or `null` at a list boundary). Reordering an item means computing a new
 * key that sits strictly between its new neighbors and writing *only* that item's `order`
 * field — never touching the shared id array — so a concurrent edit or delete of any other
 * item can never be lost or duplicated (see `apps/web/src/modules/lists/listOps.ts` and its
 * concurrency test).
 */
const ORDER_STEP = 1024;
export function orderBetween(before, after) {
    if (before === null && after === null)
        return 0;
    if (before === null)
        return after - ORDER_STEP;
    if (after === null)
        return before + ORDER_STEP;
    return (before + after) / 2;
}
//# sourceMappingURL=index.js.map